import React, { useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useSuspense } from "@rest-hooks/react";
import { ModerationResource } from "../../api/moderation";
import {
  Box,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { FilterableDataGrid } from "../video_platform/FilterableDataGrid";
import { ModerationFlagsListProps } from "./types";
import {
  formatDateTime,
  formatConfidence,
  truncateText,
} from "./utils";

interface FlagDetails {
  content: string;
  senderId: string;
  meetingId: string;
  messageId: string;
  senderName?: string;
}

export const ModerationFlagsList: React.FC<ModerationFlagsListProps> = ({ onRefresh }) => {
  const response = useSuspense(ModerationResource.getFlags, {});
  const flags = response?.data || [];

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FlagDetails | null>(null);
  const [copySnackbar, setCopySnackbar] = useState(false);

  const handleViewDetails = (flag: FlagDetails) => {
    setSelectedFlag(flag);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedFlag(null);
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbar(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "Date/Time",
      width: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "senderName",
      headerName: "Sender Name",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "senderId",
      headerName: "User ID",
      width: 120,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{truncateText(params.value, 15)}</span>
        </Tooltip>
      ),
    },
    {
      field: "meetingId",
      headerName: "Channel ID",
      width: 150,
      renderCell: (params) => {
        const channelId = params.value;
        return channelId ? (
          <Tooltip title={channelId}>
            <span>{truncateText(channelId, 15)}</span>
          </Tooltip>
        ) : (
          "-"
        );
      },
    },
    {
      field: "content",
      headerName: "Message Content",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{truncateText(params.value, 50)}</span>
        </Tooltip>
      ),
    },
    {
      field: "categories",
      headerName: "Categories",
      width: 150,
      renderCell: (params) => {
        const categories = params.value || [];
        if (categories.length === 0) return "-";

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {categories.slice(0, 2).map((category: string) => (
              <Chip
                key={category}
                label={category}
                size="small"
                sx={{ fontSize: "0.7rem", height: "20px" }}
              />
            ))}
            {categories.length > 2 && (
              <Tooltip title={categories.slice(2).join(", ")}>
                <Chip
                  label={`+${categories.length - 2}`}
                  size="small"
                  sx={{ fontSize: "0.7rem", height: "20px" }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "confidence",
      headerName: "Confidence",
      width: 110,
      renderCell: (params) => formatConfidence(params.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleViewDetails(params.row)}
          title="View details"
          sx={{ color: "#1976d2" }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <FilterableDataGrid
        rows={flags}
        columns={columns}
        filters={[]}
        searchFields={["content", "senderName", "senderId", "meetingId"]}
        searchPlaceholder="Search by content, sender, or channel..."
        height={600}
        pageSize={25}
      />

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Message Details
          <IconButton onClick={handleCloseDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFlag && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Message Content
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: "grey.100",
                    p: 2,
                    borderRadius: 1,
                    wordBreak: "break-word",
                  }}
                >
                  {selectedFlag.content}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  User ID
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      fontFamily: "monospace",
                      flex: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedFlag.senderId}
                  </Typography>
                  <Tooltip title="Copy User ID">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedFlag.senderId)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Channel ID
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      fontFamily: "monospace",
                      flex: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedFlag.meetingId}
                  </Typography>
                  <Tooltip title="Copy Channel ID">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedFlag.meetingId)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Message ID
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      fontFamily: "monospace",
                      flex: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedFlag.messageId}
                  </Typography>
                  <Tooltip title="Copy Message ID">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedFlag.messageId)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setCopySnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};
